import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EVENTS, STATUS } from "react-joyride";
import type { Controls, EventHandler, Locale } from "react-joyride";
import { useLocalStorage } from "hooks/useLocalStorage";
import { buildOnboardingSteps } from "../../Onboarding/buildOnboardingSteps";
import {
  trackOnboardingCompleted,
  trackOnboardingSkipped,
  trackOnboardingStarted,
  trackOnboardingStepViewed,
} from "../../Onboarding/onboardingAnalytics";
import type { OnboardingTourSource } from "../../Onboarding/onboardingAnalytics";
import type {
  UseSmartSurveyorOnboarding,
  UseSmartSurveyorOnboardingResult,
} from "./types";

const ONBOARDING_COMPLETED_STORAGE_KEY = "ssOnboardingCompleted";

export const useSmartSurveyorOnboarding: UseSmartSurveyorOnboarding = ({
  canEdit,
  canDelete,
  hasElements,
  hasDiagrams,
  interactionIdle,
  onShowElementsTab,
  onRevealFirstCategory,
  onStepEntered,
}) => {
  const { t } = useTranslation();

  const autoStartedRef = useRef(false);
  const controlsRef = useRef<Controls | null>(null);
  const currentStepIdRef = useRef<string | null>(null);

  const [run, setRun] = useState(false);

  const { value: isCompleted, setValue: setIsCompleted } =
    useLocalStorage<boolean>({
      key: ONBOARDING_COMPLETED_STORAGE_KEY,
      initialValue: false,
    });

  const beginTour = useCallback((source: OnboardingTourSource) => {
    autoStartedRef.current = true;
    setRun(true);
    trackOnboardingStarted(source);
  }, []);

  const startTour = useCallback(() => {
    beginTour("help-button");
  }, [beginTour]);

  const onEvent = useCallback<EventHandler>(
    (data, controls) => {
      controlsRef.current = controls;
      if (data.type === EVENTS.STEP_BEFORE) {
        const stepId =
          typeof data.step.id === "string" ? data.step.id : String(data.index);
        currentStepIdRef.current = stepId;
        trackOnboardingStepViewed(data.index, stepId);
        onStepEntered?.(stepId);
        return;
      }
      if (data.type === EVENTS.TOUR_END) {
        currentStepIdRef.current = null;
        setRun(false);
        setIsCompleted(true);
        if (data.status === STATUS.SKIPPED) {
          trackOnboardingSkipped(data.index);
        } else {
          trackOnboardingCompleted(data.size);
        }
      }
    },
    [setIsCompleted, onStepEntered],
  );

  const advanceFromStep = useCallback((stepId: string) => {
    if (currentStepIdRef.current !== stepId) return;
    controlsRef.current?.next();
  }, []);

  const steps = useMemo(
    () =>
      buildOnboardingSteps({
        t,
        canEdit,
        canDelete,
        hasDiagrams,
        hasElements,
        onShowElementsTab,
        onRevealFirstCategory,
      }),
    [
      t,
      canEdit,
      canDelete,
      hasDiagrams,
      hasElements,
      onShowElementsTab,
      onRevealFirstCategory,
    ],
  );

  const locale = useMemo<Locale>(
    () => ({
      back: t("smartSurveyor.onboarding.nav.back"),
      close: t("smartSurveyor.onboarding.nav.close"),
      last: t("smartSurveyor.onboarding.nav.last"),
      next: t("smartSurveyor.onboarding.nav.next"),
      nextWithProgress: t("smartSurveyor.onboarding.nav.nextWithProgress"),
      skip: t("smartSurveyor.onboarding.nav.skip"),
    }),
    [t],
  );

  const tourProps = useMemo<UseSmartSurveyorOnboardingResult["tourProps"]>(
    () => ({ run, steps, locale, onEvent }),
    [run, steps, locale, onEvent],
  );

  useEffect(() => {
    if (autoStartedRef.current) return;
    if (isCompleted) return;
    if (!hasElements || !interactionIdle) return;
    beginTour("auto");
  }, [isCompleted, hasElements, interactionIdle, beginTour]);

  return { tourProps, startTour, advanceFromStep, isTourActive: run };
};
